<template>
  <div class="voice-spinner-root">
    <div
      v-show="state === 'idle' || state === 'error'"
      class="voice-fab"
      :class="fabClasses"
      :style="{ width: size + 'px', height: size + 'px' }"
    >
      <div class="voice-fab__orb">
        <div class="voice-fab__ring voice-fab__ring--1"></div>
        <div class="voice-fab__ring voice-fab__ring--2"></div>
        <div class="voice-fab__ring voice-fab__ring--3"></div>
        <div class="voice-fab__center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="26" height="26">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="state !== 'idle'" class="call-screen" @click.stop @touchend.stop>
        <div class="call-screen__bg" :style="bgStyle"></div>
        <div class="call-screen__inner">
          <div class="call-screen__status">
            <span class="call-screen__status-dot" :class="statusDotClass"></span>
            <span class="call-screen__status-text">{{ statusText }}</span>
            <span v-if="callDuration > 0" class="call-screen__duration">{{ formattedDuration }}</span>
          </div>

          <div class="call-screen__transcript" ref="transcriptRef" v-if="transcript.length">
            <div
              v-for="(msg, i) in transcript"
              :key="i"
              class="call-screen__msg"
              :class="'call-screen__msg--' + msg.type"
            >
              <div class="call-screen__msg-text">{{ msg.text }}</div>
            </div>
          </div>

          <div class="call-screen__empty" v-else>
            <div class="call-screen__dots" :class="dotsClass">
              <span></span><span></span><span></span>
            </div>
          </div>

          <button class="call-screen__hangup" @click="hangup">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="28" height="28">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import VoiceCallManager from './VoiceCallManager.js'

export default {
  name: 'VoiceCallSpinner',
  props: {
    size: { type: Number, default: 56 },
    vadState: { type: String, default: 'idle' },
    callStatus: { type: String, default: '' },
    callerId: { type: String, default: '' },
    userInfo: { type: Object, default: () => ({}) },
    crmBaseUrl: { type: String, default: '' },
    authKey: { type: String, default: '' },
    userAuthKey: { type: String, default: '' },
    voiceServerUrl: { type: String, default: '' },
    sipWsUrl: { type: String, default: '/ws' },
    sipHostname: { type: String, default: '' },
    stunServers: { type: Array, default: () => [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }, { urls: 'stun:stun2.l.google.com:19302' }, { urls: 'stun:stun3.l.google.com:19302' }, { urls: 'stun:stun4.l.google.com:19302' }, { urls: 'stun:stun.cloudflare.com:3478' }] },
    turnServers: { type: Array, default: () => [] },
  },
  emits: ['stateChange', 'callStart', 'callEnd', 'error', 'stats'],
  data() {
    return {
      state: 'idle',
      sessionId: null,
      manager: null,
      isCalling: false,
      callDuration: 0,
      durationTimer: null,
      transcript: [],
      isOffline: false,
      _aborted: false,
    }
  },
  computed: {
    fabClasses() {
      return {
        'voice-fab--error': this.state === 'error',
        'voice-fab--disabled': this.state === 'connecting' || this.state === 'registering',
      }
    },
    bgStyle() {
      const colors = {
        connecting: { from: '#0f0c29', via: '#302b63', to: '#24243e' },
        registering: { from: '#1a1a2e', via: '#16213e', to: '#0f3460' },
        ringing: { from: '#2d1b69', via: '#1a1a2e', to: '#302b63' },
        'user_speaking': { from: '#004d40', via: '#00695c', to: '#00897b' },
        'ai_speaking': { from: '#4a148c', via: '#6a1b9a', to: '#8e24aa' },
        listening: { from: '#0d47a1', via: '#1565c0', to: '#1976d2' },
        error: { from: '#b71c1c', via: '#c62828', to: '#d32f2f' },
        connected: { from: '#004d40', via: '#00695c', to: '#00897b' },
      }
      const c = this.isOffline
        ? colors.error
        : colors[this.vadState !== 'idle' ? this.vadState : this.state] || colors.connecting
      return { background: `linear-gradient(135deg, ${c.from}, ${c.via}, ${c.to})` }
    },
    statusDotClass() {
      if (this.isOffline || this.state === 'error') return 'call-screen__status-dot--error'
      if (this.state === 'connected') return 'call-screen__status-dot--online'
      return 'call-screen__status-dot--pending'
    },
    statusText() {
      if (this.isOffline) return 'Нет подключения к интернету'
      switch (this.state) {
        case 'connecting': return 'Подключение...'
        case 'registering': return 'Регистрация...'
        case 'ringing': return 'Звонок...'
        case 'connected':
          if (this.vadState === 'user_speaking') return 'Вы говорите'
          if (this.vadState === 'ai_speaking') return 'Ассистент отвечает'
          if (this.vadState === 'listening') return 'Слушаю...'
          return 'Разговор'
        case 'error': return this.isOffline ? 'Нет сети' : 'Ошибка'
        default: return ''
      }
    },
    dotsClass() {
      if (this.state === 'connecting' || this.state === 'registering') return 'call-screen__dots--connecting'
      if (this.state === 'ringing') return 'call-screen__dots--ringing'
      if (this.vadState === 'user_speaking') return 'call-screen__dots--user'
      if (this.vadState === 'ai_speaking') return 'call-screen__dots--ai'
      if (this.state === 'connected') return 'call-screen__dots--connected'
      return ''
    },
    formattedDuration() {
      const m = Math.floor(this.callDuration / 60)
      const s = this.callDuration % 60
      return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0')
    },
  },
  beforeUnmount() {
    this._stopDurationTimer()
    if (this.manager) {
      this.manager.hangup()
      this.manager = null
    }
  },
  methods: {
    async handleToggle() {
      if (this.state === 'connecting' || this.state === 'registering') return
      if (this.state !== 'idle') {
        this.hangup()
        return
      }
      if (!navigator.onLine) {
        this._showOffline()
        return
      }
      await this._startCall()
    },

    hangup() {
      this._aborted = true
      this.isCalling = false
      if (this.manager) {
        this.manager.hangup()
        this.manager = null
      }
      this._stopDurationTimer()
      this.state = 'idle'
      this.sessionId = null
      this.callDuration = 0
      this.isOffline = false
      this.$emit('stateChange', 'idle')
      this.$emit('callEnd', {})
    },

    _showOffline() {
      this.isOffline = true
      this._setState('error')
      this.transcript.push({ type: 'error', text: 'Нет подключения к интернету. Пожалуйста, проверьте соединение.' })
      setTimeout(() => {
        this.state = 'idle'
        this.isOffline = false
        this.transcript = []
      }, 3000)
    },

    _playDing() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const g = ctx.createGain()
        g.connect(ctx.destination)
        g.gain.value = 0.3
        const o = ctx.createOscillator()
        o.type = 'sine'
        o.frequency.value = 880
        o.connect(g)
        o.start()
        o.stop(ctx.currentTime + 0.12)
        setTimeout(() => {
          const o2 = ctx.createOscillator()
          o2.type = 'sine'
          o2.frequency.value = 1320
          o2.connect(g)
          o2.start()
          o2.stop(ctx.currentTime + 0.15)
        }, 100)
      } catch {}
    },

    async _startCall() {
      this._aborted = false
      this._playDing()
      try {
        this.transcript = []
        this._setState('connecting')

        if (!this.authKey) throw new Error('Auth-Key not found. Login first.')
        if (!this.userAuthKey) throw new Error('User-Auth-Key not found. Login first.')

        const bridgeUrl = this.voiceServerUrl || '/pwa-api'
        const crmBase = this.crmBaseUrl || window.location.origin

        this.transcript.push({ type: 'system', text: 'Устанавливаем соединение...' })

        const ctxResp = await fetch(bridgeUrl + '/voice/session-context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Voice-Bridge-Key': this.userAuthKey },
          body: JSON.stringify({
            crmBaseUrl: crmBase,
            authKey: this.authKey,
            userAuthKey: this.userAuthKey,
            userInfo: this.userInfo,
            callerId: this.callerId || 'pwa-user',
          }),
        })
        if (this._aborted) return
        if (!ctxResp.ok) {
          const errData = await ctxResp.json().catch(() => ({}))
          throw new Error(errData.error || 'Session context registration failed')
        }
        const ctxData = await ctxResp.json()
        this.sessionId = ctxData.sessionId

        const wrcResp = await fetch(bridgeUrl + '/voice/webrtc-credentials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Voice-Bridge-Key': this.userAuthKey },
          body: JSON.stringify({
            crmBaseUrl: crmBase,
            authKey: this.authKey,
            userAuthKey: this.userAuthKey,
            sessionId: this.sessionId,
            wssUrl: this.sipWsUrl,
          }),
        })
        if (this._aborted) return
        if (!wrcResp.ok) {
          const wrcErr = await wrcResp.json().catch(() => ({}))
          throw new Error(wrcErr.error || 'WebRTC credentials fetch failed')
        }
        const wrcData = await wrcResp.json()

        this._setState('registering')

        const sipHost = this.sipHostname || window.location.hostname
        this.manager = new VoiceCallManager({
          serverUrl: this.sipWsUrl,
          username: wrcData.username,
          password: wrcData.password,
          realm: 'asterisk',
          targetUri: 'sip:' + wrcData.aiExtension + '@' + sipHost,
          fromUri: 'sip:' + wrcData.username + '@' + sipHost,
          sessionId: this.sessionId,
          iceServers: [...this.stunServers, ...this.turnServers],
          onStateChange: (s) => {
            if (this._aborted) return
            if (s === 'connected') {
              this._startDurationTimer()
              this.transcript.push({ type: 'system', text: 'Соединение установлено' })
            }
            this._setState(s === 'registered' ? 'ringing' : s)
          },
          onCallStart: () => {
            if (this._aborted) return
            this.isCalling = true
            this.$emit('callStart', { sessionId: this.sessionId })
          },
          onCallEnd: () => {
            if (this._aborted) return
            if (this.isCalling) {
              this.transcript.push({ type: 'system', text: 'Разговор завершён' })
              setTimeout(() => this.hangup(), 1500)
            }
          },
          onError: (err) => {
            if (this._aborted) return
            this.transcript.push({ type: 'error', text: 'Ошибка: ' + (err.message || err) })
            this.$emit('error', err)
            this._setState('error')
          },
          onSipMessage: () => {},
        })

        if (this._aborted) return
        await this.manager.start()
      } catch (err) {
        if (this._aborted) return
        this.transcript.push({ type: 'error', text: 'Не удалось начать звонок: ' + (err.message || err) })
        this.$emit('error', err.message || String(err))
        this._setState('error')
        setTimeout(() => {
          if (this.state === 'error') {
            this.state = 'idle'
            this.isOffline = false
          }
        }, 4000)
      }
    },

    _setState(s) {
      this.state = s
      this.$emit('stateChange', s)
    },

    _startDurationTimer() {
      this._stopDurationTimer()
      this.callDuration = 0
      this.durationTimer = setInterval(() => { this.callDuration++ }, 1000)
    },

    _stopDurationTimer() {
      if (this.durationTimer) {
        clearInterval(this.durationTimer)
        this.durationTimer = null
      }
    },
  },
}
</script>

<style scoped>
.voice-spinner-root {
  display: contents;
}

.voice-fab {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.voice-fab--disabled {
  cursor: default;
  pointer-events: none;
}

.voice-fab__orb {
  position: relative;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, #1e3c72 0%, #2a5298 100%);
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(30, 60, 114, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.voice-fab:hover .voice-fab__orb {
  transform: scale(1.08);
  box-shadow: 0 8px 32px rgba(30, 60, 114, 0.7);
}

.voice-fab:active .voice-fab__orb {
  transform: scale(0.95);
}

.voice-fab--error .voice-fab__orb {
  background: radial-gradient(circle, #cb2d3e 0%, #ef473a 100%);
  box-shadow: 0 8px 24px rgba(239, 71, 58, 0.5);
}

.voice-fab__center {
  position: relative;
  z-index: 4;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voice-fab__ring {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  opacity: 0;
  z-index: 1;
  animation: fab-pulse 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.voice-fab__ring--1 { animation-delay: 0s; }
.voice-fab__ring--2 { animation-delay: 0.8s; }
.voice-fab__ring--3 { animation-delay: 1.6s; }

.call-screen {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.call-screen__bg {
  position: absolute;
  inset: 0;
  transition: background 0.8s ease;
}

.call-screen__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  max-width: 480px;
  padding: 80px 24px 48px;
  box-sizing: border-box;
}

.call-screen__status {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255,255,255,0.85);
  font-size: 16px;
  font-weight: 500;
}

.call-screen__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #888;
  animation: pulse-dot 1.2s ease-in-out infinite;
}

.call-screen__status-dot--online {
  background: #66BB6A;
}

.call-screen__status-dot--error {
  background: #ef5350;
}

.call-screen__status-dot--pending {
  background: #FFD54F;
}

.call-screen__duration {
  margin-left: 8px;
  font-size: 14px;
  color: rgba(255,255,255,0.6);
  font-variant-numeric: tabular-nums;
}

.call-screen__transcript {
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
}

.call-screen__transcript::-webkit-scrollbar {
  width: 4px;
}

.call-screen__transcript::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.15);
  border-radius: 2px;
}

.call-screen__msg {
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  animation: msg-in 0.3s ease;
  max-width: 85%;
}

.call-screen__msg--system {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.6);
  text-align: center;
  font-size: 13px;
  padding: 8px 16px;
  align-self: center;
  max-width: 100%;
}

.call-screen__msg--error {
  background: rgba(239,83,80,0.15);
  color: #ef9a9a;
  text-align: center;
  border: 1px solid rgba(239,83,80,0.3);
  align-self: center;
  max-width: 100%;
}

.call-screen__msg--user {
  background: rgba(66,165,245,0.2);
  color: #e3f2fd;
  align-self: flex-end;
  border: 1px solid rgba(66,165,245,0.2);
}

.call-screen__msg--ai {
  background: rgba(171,71,188,0.2);
  color: #f3e5f5;
  align-self: flex-start;
  border: 1px solid rgba(171,71,188,0.2);
}

.call-screen__msg-text {
  white-space: pre-wrap;
}

.call-screen__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-screen__dots {
  display: flex;
  gap: 10px;
  align-items: center;
}

.call-screen__dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255,255,255,0.35);
  animation: dot-bounce 1.2s ease-in-out infinite;
}

.call-screen__dots span:nth-child(1) { animation-delay: 0s; }
.call-screen__dots span:nth-child(2) { animation-delay: 0.2s; }
.call-screen__dots span:nth-child(3) { animation-delay: 0.4s; }

.call-screen__dots--connecting span { background: rgba(66,165,245,0.6); }
.call-screen__dots--ringing span { background: rgba(255,213,79,0.6); }
.call-screen__dots--connected span { background: rgba(102,187,106,0.6); }
.call-screen__dots--user span { background: rgba(66,165,245,0.7); }
.call-screen__dots--ai span { background: rgba(171,71,188,0.6); }

.call-screen__hangup {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #e53935 0%, #b71c1c 100%);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 24px rgba(229,57,53,0.6);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;
}

.call-screen__hangup:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 32px rgba(229,57,53,0.8);
}

.call-screen__hangup:active {
  transform: scale(0.95);
}

@keyframes pulse-dot {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@keyframes fab-pulse {
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.6); opacity: 0; }
}

@keyframes dot-bounce {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-14px); }
}

@keyframes msg-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
